// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="global.d.ts" />
import { AnyObject } from '@sola/schema/dotcom/helpers.js';

/**
 * This method will delete the input field key at any level from the input object
 */
export const deleteObjKey = (obj: AnyObject, key: string) => {
  Object.keys(obj).map(async (objKey) => {
    if (objKey === key) {
      // eslint-disable-next-line no-param-reassign
      delete obj[objKey];
    } else if (typeof obj[objKey] === 'object') {
      deleteObjKey(obj[objKey], key);
    }
  });
};

/**
 * Check if a variable is an object.
 */
export const isObject = (obj: unknown): obj is AnyObject => {
  return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
};

/**
 * This method creates a deep clone of the input object to be used as an input
 * to the `deleteObjKey` method so as to not modify the original object.
 */
export const objectSansProperty = async (
  keys: string | string[],
  object: AnyObject,
): Promise<AnyObject> => {
  const clonedObj = structuredClone(object);

  const keysArr = Array.isArray(keys) ? keys : [keys];
  keysArr.forEach((key): void => {
    deleteObjKey(clonedObj, key);
  });

  return clonedObj;
};

/**
 * Check if input value is between a min & max value
 */
export const isBetween = (num: number, min: number, max: number) => {
  return num >= min && num <= max;
};

/**
 * @description check if object has keys
 */
export const isEmptyObject = (value: unknown) => {
  return value && typeof value === 'object' && Object.keys(value)?.length === 0;
};

// TODO :: don't love this....needs rework...brain failing...shutting downasljdapiodfa;dslj
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isEmptyObjectDeep = (obj: Record<string, any>) => {
  // Check if the object is null or undefined
  if (obj === null || obj === undefined) {
    return true;
  }

  // Check if the object has no properties
  if (Object.keys(obj).length === 0) {
    return true;
  }

  const hasValue = Object.keys(obj).some((key) => {
    if (typeof obj[key] === 'object') {
      if (!isEmptyObjectDeep(obj[key])) {
        return true;
      }
    } else if (obj[key] !== undefined && obj[key] !== null) {
      return true;
    }

    return false;
  });

  return !hasValue;
};

/**
 * @description reset global variable data
 */
export const resetGlobalData = async (
  contentKey: string,
  obj = global.contentData,
) => {
  if (contentKey) {
    objectSansProperty(contentKey, obj);
  } else {
    // eslint-disable-next-line no-param-reassign
    obj = {};
  }
};

/**
 * Scrolls the window to the top with a smooth animation.
 */
export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

export const isTwoHundredRes = (num: number) => {
  return isBetween(num, 200, 299);
};

/**
 * @description Get the ordinal ("st", "th", etc) for a number
 * @TODO: Use i18n instead. See: https://www.i18next.com/translation-function/plurals#ordinal-plurals
 */
export const getThStNdRd = (num: number) => {
  switch (num) {
    case 1:
    case 21:
    case 31:
      return `${num}st`;
    case 2:
    case 22:
      return `${num}nd`;
    case 3:
    case 23:
      return `${num}rd`;
    default:
      return `${num}th`;
  }
};

// Base64 handling - from https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem.
const bytesToBase64 = (bytes: Uint8Array) => {
  const binString = String.fromCodePoint(...bytes);
  return btoa(binString);
};

const base64ToBytes = (base64: string) => {
  const binString = atob(base64);
  return Uint8Array.from(binString, (m) => m.codePointAt(0) ?? 0);
};

/**
 * @description Encode a text string to a base64 string
 */
export const base64EncodeString = (text?: string) => {
  // This could have some silent-failing with lone surrogates which could be mitigated using isWellFormed.
  // But for our purposes we want to encode anyway with the silent failure - https://web.dev/articles/base64-encoding
  if (!text) {
    return '';
  }
  return bytesToBase64(new TextEncoder().encode(text));
};

/**
 * @description Encode an object to a base64 string
 */
export const base64UrlEncodeObject = (objectToBeEncoded: object): string => {
  try {
    const base64 = base64EncodeString(JSON.stringify(objectToBeEncoded));

    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch {
    return '';
  }
};

/**
 * @description Decode a base64 string to a text string
 */
export const base64DecodeString = (base64?: string) => {
  if (!base64) {
    return '';
  }
  return new TextDecoder().decode(base64ToBytes(base64));
};

export const base64DecodeObject = <T>(
  base64String: string | undefined,
): T | undefined => {
  if (!base64String) {
    return undefined;
  }
  try {
    return JSON.parse(base64DecodeString(base64String)) as T;
  } catch {
    return undefined;
  }
};

export const isDevelopmentEnvironment = process.env.NODE_ENV === 'development';
